

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  CircularProgress,
  Alert,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';

const userInitials = (name = '', lastName = '') => {
  return (name?.charAt(0) || '') + (lastName?.charAt(0) || '');
};

const getAvatarColor = (name = '') => {
  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'];
  return colors[(name?.charCodeAt(0) || 0) % colors.length];
};

export const AssignPatientModal = ({
  open, onClose, patient, handleAssignPatient, assignees = [], loading = false,
}) => {
  const [selectedAssignee, setSelectedAssignee] = useState(null); // store full object
const [error, setError] = useState('');

useEffect(() => {
  if (open) {
    setSelectedAssignee(null);
    setError('');
  }
}, [open]);

  if (!patient) return null;

  const handleSubmit = () => {
    if (!selectedAssignee) {
      setError('Please select a user to assign');
      return;
    }
    handleAssignPatient(selectedAssignee);
    setSelectedAssignee(null);
  };

  const patientName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.12)' } }}>

      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #B01030 0%, #8A0D25 100%)',
        color: 'white', fontWeight: 700, fontSize: '1.1rem',
        display: 'flex', alignItems: 'center', gap: 1.5, py: 2.5, px: 3,
      }}>
        <Icon icon="solar:user-plus-bold" style={{ fontSize: 20 }} />
        Assign Patient
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Patient Info Card */}
        <Box sx={{
          backgroundColor: '#FCF0F2', border: '1.5px solid #E8C0C8',
          borderRadius: 2, px: 2, py: 1.5, mb: 2.5,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Avatar sx={{ width: 40, height: 40, backgroundColor: '#B01030', color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>
            {patientName.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0D1B2A' }}>
              {patientName || 'Unknown Patient'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#5A6A7A' }}>Assigning to...</Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: '0.85rem' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {!loading && assignees.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontSize: '0.85rem' }}>
            No users available to assign.
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1.5 }}>
            <CircularProgress size={36} sx={{ color: '#B01030' }} />
            <Typography sx={{ color: '#5A6A7A', fontSize: '0.85rem' }}>Loading users...</Typography>
          </Box>
        )}

        {!loading && assignees.length > 0 && (
          <Box>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
              color: '#5A6A7A', letterSpacing: '0.05em', mb: 1.5,
            }}>
              Select a User
            </Typography>

            {/* ✅ RadioGroup value now tracks the full object's ID */}
            <RadioGroup
              value={selectedAssignee?._id || selectedAssignee?.id || ''}
              onChange={(e) => {
                const user = assignees.find(a => (a._id || a.id) === e.target.value);
                setSelectedAssignee(user || null);
                setError('');
              }}
            >
              <List sx={{ p: 0 }}>
                {assignees.map((user) => {
                  const userId = user._id || user.id;
                  const displayName =
                    user.name ||
                    `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                    user.email || 'Unknown User';

                  // ✅ Compare against the selected object's ID, not the object itself
                  const isSelected = (selectedAssignee?._id || selectedAssignee?.id) === userId;

                  return (
                    <ListItem key={userId} disablePadding sx={{
                      mb: 0.75, borderRadius: 2,
                      border: `1.5px solid ${isSelected ? '#B01030' : '#E2E8EF'}`,
                      backgroundColor: isSelected ? '#FCF0F2' : 'transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': { backgroundColor: '#F7F9FB', borderColor: '#B01030' },
                    }}>
                      <ListItemButton component="label" sx={{ py: 1.25, px: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                          <FormControlLabel
                            value={userId}
                            control={<Radio size="small" sx={{ color: '#E2E8EF', '&.Mui-checked': { color: '#B01030' } }} />}
                            label=""
                            sx={{ m: 0 }}
                          />
                        </ListItemIcon>

                        <Avatar sx={{
                          width: 36, height: 36,
                          backgroundColor: getAvatarColor(displayName),
                          color: 'white', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
                        }}>
                          {userInitials(user.firstName || user.name, user.lastName)}
                        </Avatar>

                        <ListItemText
                          primary={<Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#0D1B2A', lineHeight: 1.3 }}>{displayName}</Typography>}
                          secondary={user.email
                            ? <Typography sx={{ fontSize: '0.75rem', color: '#8A9BAC', lineHeight: 1, mt: 0.25 }}>{user.email}</Typography>
                            : null}
                          sx={{ m: 0 }}
                        />

                        {user.status === 'active' && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#0D8A5E', flexShrink: 0 }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </RadioGroup>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E2E8EF', gap: 1 }}>
        <Button onClick={onClose} sx={{ color: '#5A6A7A', borderRadius: 2, fontWeight: 500, textTransform: 'none', px: 2.5 }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained"
          disabled={!selectedAssignee || loading}
          sx={{
            backgroundColor: '#B01030', color: 'white', borderRadius: 2,
            fontWeight: 700, textTransform: 'none', px: 3,
            boxShadow: '0 4px 14px rgba(176,16,48,0.35)',
            '&:hover': { backgroundColor: '#8A0D25' },
            '&:disabled': { backgroundColor: '#A8B8C8', color: '#F7F9FB', boxShadow: 'none' },
          }}>
          {loading
            ? <><CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />Assigning...</>
            : <><Icon icon="solar:check-circle-bold" style={{ marginRight: 6, fontSize: 16 }} />Assign Patient</>
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// import {
//   Box,
//   Button,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   Menu,
//   MenuItem,
//   Typography
// } from '@mui/material'
// import { useEffect, useState } from 'react'
// import { useDispatch, useSelector } from 'react-redux'
// import { getAssignees } from 'src/redux/patient/patient.action'

// export const AssignPatientModal = ({ open, onClose, handleAssignPatient, defaultAssignee }) => {
//   const dispatch = useDispatch()
//   const { assignees } = useSelector(state => state.patient)
//   const [anchorEl, setAnchorEl] = useState(null)
//   const [selectedAssignee, setSelectedAssignee] = useState(null)

//   const handleDropdownOpen = event => {
//     setAnchorEl(event.currentTarget)
//   }

//   const handlePatientSelect = assignee => {
//     setSelectedAssignee(assignee)
//     setAnchorEl(null)
//   }

//   const handleDropdownClose = () => {
//     setAnchorEl(null)
//   }

//   const handleModalClose = () => {
//     onClose()
//     setSelectedAssignee(null)
//   }

//   const handleConfirm = () => {
//     handleAssignPatient(selectedAssignee._id)
//     handleModalClose()
//   }

//   useEffect(() => {
//     dispatch(getAssignees())
//   }, [])

//   return (
//     <Dialog open={open} onClose={handleModalClose} sx={{ '& .MuiPaper-root': { width: '512px', mt: 4 } }}>
//       <DialogTitle>Assign Patient</DialogTitle>
//       <DialogContent>
//         <Box>
//           <Button onClick={handleDropdownOpen}>
//             {defaultAssignee && defaultAssignee}
//             {!defaultAssignee && selectedAssignee && selectedAssignee.email}
//             {!defaultAssignee && !selectedAssignee && 'Select an assignee for this patient'}
//           </Button>
//         </Box>
//         <Menu
//           anchorEl={anchorEl}
//           open={Boolean(anchorEl)}
//           onClose={() => handleDropdownClose()}
//           sx={{ '& .MuiMenu-paper': { width: 'fit-content', mt: 4 } }}
//           anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
//           transformOrigin={{ vertical: 'top', horizontal: 'center' }}
//         >
//           {assignees?.map(assignee => (
//             <MenuItem
//               onClick={() => handlePatientSelect(assignee)}
//               sx={{
//                 py: 2,
//                 px: 4,
//                 color: 'text.secondary',
//                 '& svg': { mr: 2, fontSize: '1.25rem', color: 'text.secondary' }
//               }}
//             >
//               {assignee.email}
//             </MenuItem>
//           ))}
//         </Menu>
//       </DialogContent>
//       <DialogActions>
//         <Button onClick={handleModalClose} color='primary'>
//           Cancel
//         </Button>
//         <Button onClick={handleConfirm} disabled={Boolean(!selectedAssignee)} color='secondary'>
//           Confirm
//         </Button>
//       </DialogActions>
//     </Dialog>
//   )
// }
